import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { DataGrid, DataGridColumn, TaskDialog, Badge, Spinner } from '@repo/components';
import { Task } from '@repo/shared/domain';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getCompanyTasks, getEventTasksByGroup } from 'src/api/company';
import { useConfig } from 'src/hooks/useConfig';
import { constructQuery } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/tasks/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		company_page: (search.company_page as number) || 0,
		company_sort: (search.company_sort as string) || 'updatedAt:desc',
		event_page: (search.event_page as number) || 0,
		event_sort: (search.event_sort as string) || 'updatedAt:desc',
	}),
});

const variants = {
	done: 'success',
	pending: 'outline',
	todo: 'default',
	expiryWarning: 'warning',
	expired: 'error',
	failed: 'error',
} as const;

const states = {
	done: 'Done',
	pending: 'Pending',
	todo: 'Todo',
	expiryWarning: 'Almost Expiring',
	expired: 'Expired',
	failed: 'Failed',
	superseded: 'Superseded',
};

function RouteComponent() {
	const { groupId } = Route.useParams();
	const { company_page, company_sort, event_page, event_sort } = Route.useSearch();
	const navigate = useNavigate();
	const updateQueryParams = useUpdateQueryParams();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const companyQuery = constructQuery({ page: company_page, sort: company_sort });
	const eventQuery = constructQuery({ page: event_page, sort: event_sort });

	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};

	const { data: companyTasks, isLoading: companyTasksLoading } = useQuery({
		queryKey: ['company-tasks-list', groupId, { company_page, company_sort }],
		queryFn: () => getCompanyTasks(groupId, companyQuery),
		enabled: !userLoading,
	});

	const { data: eventTasks, isLoading: eventTasksLoading } = useQuery({
		queryKey: ['company-event-tasks-list', groupId, { event_page, event_sort }],
		queryFn: () => getEventTasksByGroup(groupId, eventQuery),
		enabled: !userLoading,
	});

	const [selectedTask, setSelectedTask] = useState<Task | null>(null);
	const usesSubContracting = featureFlags?.USES_SUBCONTRACTING || false;
	useBreadcrumbLevel(1, 'Tasks');

	if (userLoading) {
		return null;
	}

	const revalidateTasks = () => {
		queryClient.invalidateQueries({ queryKey: ['company-tasks-list', groupId] });
		queryClient.invalidateQueries({ queryKey: ['company-event-tasks-list', groupId] });
		queryClient.invalidateQueries({ queryKey: ['company-tasks', groupId] });
		queryClient.invalidateQueries({ queryKey: ['company-event-tasks', groupId] });
	};

	const handleCompanyTasksSort = ([sort]: any[]) => {
		updateQueryParams({ company_sort: sortToString(sort) });
	};

	const handleEventTasksSort = ([sort]: any[]) => {
		updateQueryParams({ event_sort: sortToString(sort) });
	};

	const handleTaskDialogClose = () => {
		setSelectedTask(null);
		revalidateTasks();
	};

	const handleCompanyTasksPaginationChange = ({ page }: { page: number }) => {
		updateQueryParams({ company_page: page });
	};

	const handleEventTasksPaginationChange = ({ page }: { page: number }) => {
		updateQueryParams({ event_page: page });
	};

	const getColumns = (type: 'company' | 'event'): DataGridColumn<Task>[] => {
		const baseColumns: DataGridColumn<Task>[] = [
			{ headerName: 'Description', field: 'name', flex: 1 },
			...(type === 'event' && usesSubContracting
				? [
						{
							field: 'event.project',
							headerName: 'Project',
							width: 150,
							renderCell: (params: any) => {
								if (!params.value?.name) return '-';
								return (
									<Badge variant={params.value.status === 'accepted' ? 'success' : 'secondary'}>
										{params.value.name}
									</Badge>
								);
							},
						},
					]
				: []),
			...(type === 'event'
				? [
						{
							field: 'event',
							headerName: 'Event',
							width: 150,
							renderCell: (params: any) => {
								if (!params.value?.name) return '-';
								return (
									<Link
										to="/company/$groupId/events/$eventId/tasks"
										params={{ groupId, eventId: params.value.id }}
										onClick={(e: any) => e?.stopPropagation()}
									>
										<Badge variant={params.value.status === 'accepted' ? 'success' : 'secondary'}>
											{params.value.name}
										</Badge>
									</Link>
								);
							},
						},
					]
				: []),
			{
				field: 'createdAt',
				headerName: 'Assigned',
				width: 120,
				valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
			},
			{
				field: 'updatedAt',
				headerName: 'Last updated',
				width: 120,
				valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
			},
			{
				field: 'validTo',
				headerName: 'Valid to',
				width: 100,
				valueFormatter: (params) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
			},
			{
				field: 'status',
				headerName: 'Status',
				renderCell: (cell) => <Badge variant={variants[cell.row.status]}>{states[cell.row.status]}</Badge>,
				sortable: false,
				width: 175,
			},
		];
		return baseColumns;
	};

	const handleRowClick = ({ row }: { row: Task }) => {
		if (row.status === 'todo') {
			setSelectedTask(row);
		}
		if (row.status === 'done') {
			navigate({
				to: '/company/$groupId/tasks/$taskId',
				params: { groupId: row.groupId, taskId: row.id },
			});
		}
	};

	return (
		<>
			<div className="mt-4">
				<Typography variant="h5" component="h1" className="mb-4">
					Company Tasks
				</Typography>
				<Paper className="mt-2" variant="outlined">
					<DataGrid
						data={companyTasks?.data || []}
						columns={getColumns('company')}
						isLoading={companyTasksLoading}
						paginationMode="server"
						sortingMode="server"
						onPaginationModelChange={handleCompanyTasksPaginationChange}
						onSortModelChange={handleCompanyTasksSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(company_sort)]}
						onRowClick={handleRowClick}
						pagination
						rowCount={companyTasks?.count || 0}
						pageSize={companyTasks?.pageSize}
						page={companyTasks?.page}
						getRowClassName={(p) => p.row.markedForReview && 'error'}
						onReload={revalidateTasks}
					/>
				</Paper>
			</div>
			<div className="mt-4">
				<Typography variant="h5" component="h1" className="mb-4">
					Event Tasks
				</Typography>
				<Paper className="mt-2" variant="outlined">
					<DataGrid
						data={eventTasks?.data || []}
						columns={getColumns('event')}
						isLoading={eventTasksLoading}
						paginationMode="server"
						sortingMode="server"
						onPaginationModelChange={handleEventTasksPaginationChange}
						onSortModelChange={handleEventTasksSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(event_sort)]}
						onRowClick={handleRowClick}
						pagination
						rowCount={eventTasks?.count || 0}
						pageSize={eventTasks?.pageSize}
						page={eventTasks?.page}
						onReload={revalidateTasks}
					/>
				</Paper>
			</div>
			<TaskDialog task={selectedTask} onClose={handleTaskDialogClose} companyId={groupId} forCompanyPortal={true} />
		</>
	);
}
